import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _toUpper from 'lodash/toUpper';
// Serialization
import { deserialize } from 'serializr';
import { distributionSchema } from './schemas';
// Color
import { statusPalette } from '../components/core/colors';
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';
import { findInList, readableFileSize } from '../utils/Tools';

/**
* Serialization
*/
const deserializeDistribution = (data) => deserialize(distributionSchema, data);

// Must use FormData for serializer because of file sending
const serializeDistribution = ({
  type, version, comment, disabled,
  fileOpacProduction, fileOpacCompleteUpdate, fileOpacIncrementalUpdate, fileStmProduction,
}) => {
  const data = new FormData();
  data.append('distribution[disabled]', !!disabled);
  if (type) {
    data.append('distribution[type]', type);
  }
  data.append('distribution[version]', version);
  data.append('distribution[comment]', comment);
  if (fileOpacProduction) {
    data.append('distribution[file_opac_production]', fileOpacProduction);
  }
  if (fileOpacCompleteUpdate) {
    data.append('distribution[file_opac_complete_update]', fileOpacCompleteUpdate);
  }
  if (fileOpacIncrementalUpdate) {
    data.append('distribution[file_opac_incremental_update]', fileOpacIncrementalUpdate);
  }
  if (fileStmProduction) {
    data.append('distribution[file_stm_production]', fileStmProduction);
  }
  return data;
};

/**
* Lists
*/
const distributionTypesList = [
  {
    key: 'opac',
    value: 'opac',
    labelId: 'distribution.type.opac',
  },
  {
    key: 'stm',
    value: 'stm',
    labelId: 'distribution.type.stm',
  },
];

const distributionFileStatusesList = [
  {
    key: 'uploaded',
    value: true,
    labelId: 'file.status.uploaded',
    icon: 'fileUploaded',
    color: statusPalette.success,
  },
  {
    key: 'uploading',
    value: false,
    labelId: 'file.status.uploading',
    icon: 'fileUploading',
    color: statusPalette.warning,
  },
];

/**
* Utils
*/

const distributionDesc = (distribution) => (distribution ? `${_toUpper(distribution.type)} ${distribution.version}` : '');
const distributionTitle = (distribution) => distributionDesc(distribution);
const distributionSmallDesc = (distribution) => (distribution ? _toUpper(distribution.type) : '');
const distributionFullDesc = (distribution) => (distribution ? `${distributionDesc(distribution)}${distribution.comment ? ` (${distribution.comment})` : ''}` : '');

const distributionIsLocked = (distribution) => (distribution.devices_quantity > 0 || distribution.rollouts_quantity > 0);

const distributionFileDesc = (file) => {
  const { unit, value } = readableFileSize(file.size);
  return `${file.name} (${value} ${unit})`;
};
const distributionFileStatus = (file) => findInList(distributionFileStatusesList, 'value', file, 'uploaded') || distributionFileStatusesList[1];

/**
* Model
*/
const distribution = {
  // Name and icon
  name: 'distribution',
  icon: 'distribution',
  documentationUrl: 'https://onepark.atlassian.net/wiki/spaces/OPAC/pages/2076114967/New+release',

  // Serialization
  deserialize: deserializeDistribution,
  serialize: serializeDistribution,

  // Lists
  typesList: distributionTypesList,
  fileStatusesList: distributionFileStatusesList,

  // Utils
  title: distributionTitle,
  desc: distributionDesc,
  smallDesc: distributionSmallDesc,
  fullDesc: distributionFullDesc,
  isLocked: distributionIsLocked,
  fileDesc: distributionFileDesc,
  fileStatus: distributionFileStatus,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'date', dir: 'desc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'version',
      operator: SEARCHFIELD_TYPES.string.defaultOperator('like'),
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 2,
      md: 5,
      lg: 5,
      xl: 6,
    }),
    columns: [
      {
        key: 'type',
        labelId: 'field.type',
        field: 'type',
        sortable: true,
      },
      {
        key: 'version',
        labelId: 'field.version',
        field: 'version',
        sortable: true,
      },
      {
        key: 'date',
        labelId: 'field.date',
        field: 'date',
        sortable: true,
        className: 'hidden-sm-down',
      },
      {
        key: 'comment',
        labelId: 'field.comments',
        field: 'comment',
        sortable: false,
        className: 'hidden-lg-down',
      },
      {
        key: 'files',
        labelId: 'field.files',
        field: 'files',
        sortable: false,
        className: 'hidden-sm-down table-content-2',
      },
      {
        key: 'dependencies',
        labelId: 'field.dependencies',
        field: 'dependencies',
        sortable: false,
        className: 'hidden-sm-down table-content-2',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'type',
        labelId: 'field.type',
        field: 'type',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        excludeFilterFields: ['type'],
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'type',
        },
      },
      {
        key: 'version',
        labelId: 'field.version',
        field: 'version',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        scope: 'like',
        filter: {
          field: 'version',
        },
      },
      {
        key: 'begin',
        labelId: 'field.afterDate',
        field: 'begin',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'date',
        },
      },
      {
        key: 'end',
        labelId: 'field.beforeDate',
        field: 'end',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'date',
        },
      },
    ],
  },

  // History columns
  historyColumns: [
    {
      key: 'desc',
      labelId: 'field.distribution',
      field: 'distribution',
      sortable: false,
    },
    {
      key: 'begin',
      labelId: 'field.beginDate',
      field: 'begin',
      sortable: false,
    },
    {
      key: 'end',
      labelId: 'field.endDate',
      field: 'end',
      sortable: false,
    },
  ],
};

module.exports = distribution;



// WEBPACK FOOTER //
// ./models/distribution.js